import { useEffect, useMemo, useState } from "react";
import { mumsApi } from "./mumsApi";
import { MumDrawer, PhotoField, FormSection } from "./MumDrawer";
import { Plus, Search, Edit3, Trash2, Loader2, Flower } from "lucide-react";
import { toast } from "sonner";

const FLOWER_TYPES = ["Zinnia", "Cosmos", "Ranunculus", "Tulip", "Daffodil", "Sweet Pea", "Snapdragon", "Anemone", "Other"];
const STATUSES = ["Growing", "Budding", "Flowering", "Finished", "Lifted", "Seedling"];
const STATUS_TONES = {
  Growing: "bg-[var(--mum-sky)] text-[var(--mum-sapphire)]",
  Budding: "bg-[var(--mum-cornflower)]/30 text-[var(--mum-sapphire)]",
  Flowering: "bg-[var(--mum-cobalt)] text-[var(--mum-ivory)]",
  Finished: "bg-[var(--mum-silver)] text-[var(--mum-cobalt)]",
  Lifted: "bg-[var(--mum-sapphire)]/20 text-[var(--mum-sapphire)]",
  Seedling: "bg-[var(--mum-sky)] text-[var(--mum-sapphire)]",
};
const empty = {
  flower_type: "Zinnia", variety: "", photos: [], height: "", colour: "",
  garden_bed: "", row: "", date_planted: "", flowering_period: "",
  status: "Growing", stem_value: 0, notes: "",
};

export default function MumsFlowers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const [drawer, setDrawer] = useState(null);

  const load = async () => { setLoading(true); try { setItems(await mumsApi.listFlowers()); } finally { setLoading(false); } };
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const r = await mumsApi.listFlowers(); if (!cancelled) setItems(r); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((i) => (filterType ? i.flower_type === filterType : true))
      .filter((i) => (q ? [i.flower_type, i.variety, i.colour, i.garden_bed].some((s) => (s || "").toLowerCase().includes(q)) : true));
  }, [items, search, filterType]);

  const onDelete = async (id) => {
    if (!window.confirm("Remove this flower record?")) return;
    await mumsApi.deleteFlower(id);
    toast.success("Flower removed");
    await load();
  };

  return (
    <div className="space-y-6" data-testid="mums-flowers-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="mum-chip"><Flower size={12} /> Tab 4</div>
          <h1 className="font-display text-4xl mt-2">Mum&apos;s Flowers</h1>
          <p className="font-display italic text-[var(--mum-cobalt)] text-sm mt-1">{filtered.length} record{filtered.length === 1 ? "" : "s"}</p>
        </div>
        <button className="mum-btn" onClick={() => setDrawer("new")} data-testid="mums-flower-add"><Plus size={15} /> Add Flower</button>
      </header>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--mum-cobalt)]/60" />
          <input className="mum-field pl-9" placeholder="Search by type, variety, colour, bed…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mums-flower-search" />
        </div>
        <select className="mum-field sm:w-56" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="">All flower types</option>
          {FLOWER_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12"><Loader2 size={24} className="animate-spin mx-auto text-[var(--mum-cobalt)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="mums-card-quiet p-10 text-center" data-testid="mums-flower-empty">
          <Flower size={36} className="text-[var(--mum-cobalt)]/60 mx-auto" />
          <p className="font-display italic text-[var(--mum-cobalt)] text-lg mt-3">No flowers recorded yet.</p>
          <button className="mum-btn mt-5" onClick={() => setDrawer("new")}><Plus size={15} /> Add first flower</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mums-flower-grid">
          {filtered.map((f) => (
            <article key={f.id} className="mums-card overflow-hidden flex flex-col" data-testid={`flower-${f.id}`}>
              <div className="aspect-[4/3] bg-[var(--mum-sky)]/50">
                {(f.photos || [])[0] ? (
                  <img src={f.photos[0]} alt={f.flower_type} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Flower size={36} className="text-[var(--mum-cobalt)]/50" /></div>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display text-xl text-[var(--mum-sapphire)]">{f.flower_type}{f.variety ? ` — ${f.variety}` : ""}</h3>
                  <span className={`text-[0.65rem] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_TONES[f.status] || "bg-[var(--mum-sky)]"}`}>{f.status}</span>
                </div>
                <p className="text-xs text-[var(--mum-cobalt)]/75 mt-1">
                  {[f.colour, f.height, f.garden_bed && `Bed ${f.garden_bed}`].filter(Boolean).join(" · ")}
                </p>
                {f.flowering_period && <p className="text-xs text-[var(--mum-cobalt)] mt-0.5">Flowering: {f.flowering_period}</p>}
                {f.notes && <p className="text-sm mt-2 line-clamp-3">{f.notes}</p>}
                <div className="flex gap-2 mt-auto pt-3">
                  <button className="mum-btn-ghost flex-1" onClick={() => setDrawer({ edit: f })} data-testid={`flower-edit-${f.id}`}><Edit3 size={13} /> Edit</button>
                  <button className="mum-btn-ghost" onClick={() => onDelete(f.id)} data-testid={`flower-delete-${f.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <MumDrawer open={drawer !== null} onClose={() => setDrawer(null)} title={drawer?.edit ? "Edit Flower" : "Add Flower"} testid="mums-flower-drawer">
        <FlowerForm initial={drawer?.edit || empty} onClose={() => setDrawer(null)} onSaved={async () => { setDrawer(null); await load(); }} />
      </MumDrawer>
    </div>
  );
}

function FlowerForm({ initial, onClose, onSaved }) {
  const editing = !!initial.id;
  const [form, setForm] = useState({ ...empty, ...initial });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.flower_type.trim()) { toast.error("Flower type is required"); return; }
    setBusy(true);
    try {
      const payload = { ...form, stem_value: Number(form.stem_value) || 0 };
      if (editing) await mumsApi.updateFlower(initial.id, payload);
      else await mumsApi.createFlower(payload);
      toast.success(editing ? "Flower updated" : "Flower added");
      onSaved();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Couldn't save the flower");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="flower-form">
      <FormSection title="The Flower">
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Type *</span>
            <select className="mum-field mt-1.5" value={form.flower_type} onChange={(e) => set("flower_type", e.target.value)} data-testid="flower-type" required>
              {FLOWER_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Variety</span>
            <input className="mum-field mt-1.5" placeholder="e.g. Queen Lime, Cupcakes" value={form.variety || ""} onChange={(e) => set("variety", e.target.value)} data-testid="flower-variety" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Status</span>
            <select className="mum-field mt-1.5" value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="flower-status">
              {STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Colour</span>
            <input className="mum-field mt-1.5" value={form.colour || ""} onChange={(e) => set("colour", e.target.value)} />
          </label>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Height</span>
            <input className="mum-field mt-1.5" placeholder="80 cm" value={form.height || ""} onChange={(e) => set("height", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Bed</span>
            <input className="mum-field mt-1.5" value={form.garden_bed || ""} onChange={(e) => set("garden_bed", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Row</span>
            <input className="mum-field mt-1.5" value={form.row || ""} onChange={(e) => set("row", e.target.value)} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Date planted</span>
            <input type="date" className="mum-field mt-1.5" value={form.date_planted || ""} onChange={(e) => set("date_planted", e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Flowering period</span>
            <input className="mum-field mt-1.5" placeholder="Dec–Mar" value={form.flowering_period || ""} onChange={(e) => set("flowering_period", e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.18em] text-[var(--mum-cobalt)]">Stem value ($)</span>
          <input type="number" min="0" step="0.5" className="mum-field mt-1.5" value={form.stem_value} onChange={(e) => set("stem_value", e.target.value)} />
        </label>
      </FormSection>

      <FormSection title="Photos">
        <PhotoField label="Photos" value={form.photos} onChange={(v) => set("photos", v)} multi testid="flower-photos" />
      </FormSection>

      <FormSection title="Notes">
        <textarea className="mum-field" rows={4} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} />
      </FormSection>

      <div className="flex gap-2 sticky bottom-0 bg-[var(--mum-paper)] pt-3 -mx-5 px-5 pb-1 border-t border-[var(--mum-sky)]">
        <button type="button" className="mum-btn-ghost flex-1" onClick={onClose}>Cancel</button>
        <button type="submit" className="mum-btn flex-1" disabled={busy} data-testid="flower-save">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {editing ? "Save changes" : "Add flower"}
        </button>
      </div>
    </form>
  );
}
